import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
} from "react";

import { useAuth } from "./AuthContext";
import { useRegionalUserContext } from "../hooks/useRegionalUserContext";
import {
  normalizeRegionalRecordingModeConfig,
  normalizeRegionalUserProfile,
} from "../utils/backend/pdxBackendClient";

const DEFAULT_CONTEXT_VALUE = {
  backendUser: null,
  accountContext: null,
  profile: null,
  productPresentationProfile: null,
  contextMetadata: null,
  contextVersion: 0,
  profileSummary: null,
  recordingModeConfig: null,
  recordingModeConfigError: null,
  legalStatus: null,
  legalStatusError: null,
  loading: false,
  error: null,
  profileSummaryLoading: false,
  profileSummaryError: null,
  refresh: () => Promise.resolve(null),
  applyProfile: () => {},
  applyLegalStatus: () => {},
};

const RegionalBackendContext = createContext(DEFAULT_CONTEXT_VALUE);

/**
 * Shared regional backend bootstrap context for the authenticated app shell.
 *
 * This bridges Firebase client auth to the regional backend's verified user
 * context so the app can progressively depend on backend-approved identity and
 * account state rather than only the client SDK user object.
 *
 * @param {{ children: React.ReactNode }} props - Provider props.
 * @returns {JSX.Element} Context provider element.
 */
export function RegionalBackendProvider({ children }) {
  const { user } = useAuth();
  const {
    loading,
    data,
    error,
    reload,
    applyData,
    settledRequestVersion,
  } = useRegionalUserContext({
    enabled: Boolean(user?.uid),
  });

  const backendUser = data?.user ?? null;
  const accountContext = data?.accountContext ?? null;
  const profile = normalizeRegionalUserProfile(data?.profile);
  const productPresentationProfile = data?.productPresentationProfile ?? null;
  const contextVersion = Number(settledRequestVersion) || 0;
  const profileSummary = data?.profileSummary ?? null;
  const contextMetadata = data?.contextMetadata ?? null;
  const recordingModeConfig = normalizeRegionalRecordingModeConfig(
    data?.recordingModeConfig,
  );
  const recordingModeConfigError = data?.recordingModeConfigError ?? null;
  const legalStatus = data?.legalStatus ?? null;
  const legalStatusError = data?.legalStatusError ?? null;
  const isAuthenticatedBootstrapLoading =
    Boolean(user?.uid) && (loading || (!data && !error));

  const refresh = useCallback(() => reload(), [reload]);
  const applyProfile = useCallback(
    (profilePatch) => {
      if (!profilePatch || typeof profilePatch !== "object") {
        return;
      }

      applyData((currentData) => {
        const nextData =
          currentData && typeof currentData === "object"
            ? { ...currentData }
            : {};
        const currentProfile =
          nextData.profile && typeof nextData.profile === "object"
            ? nextData.profile
            : {};

        nextData.profile = normalizeRegionalUserProfile({
          ...currentProfile,
          ...profilePatch,
        });

        return nextData;
      });
    },
    [applyData],
  );
  const applyLegalStatus = useCallback(
    (legalStatus) => {
      if (!legalStatus || typeof legalStatus !== "object") {
        return;
      }

      applyData((currentData) => {
        const nextData =
          currentData && typeof currentData === "object"
            ? { ...currentData }
            : {};

        nextData.legalStatus = legalStatus;
        nextData.legalStatusError = null;

        return nextData;
      });
    },
    [applyData],
  );

  const value = useMemo(
    () => ({
      backendUser,
      accountContext,
      profile,
      productPresentationProfile,
      contextMetadata,
      contextVersion,
      profileSummary,
      recordingModeConfig,
      recordingModeConfigError,
      legalStatus,
      legalStatusError,
      loading: isAuthenticatedBootstrapLoading,
      error,
      profileSummaryLoading: isAuthenticatedBootstrapLoading,
      profileSummaryError: error,
      refresh,
      applyProfile,
      applyLegalStatus,
    }),
    [
      applyProfile,
      applyLegalStatus,
      accountContext,
      backendUser,
      contextVersion,
      contextMetadata,
      error,
      isAuthenticatedBootstrapLoading,
      legalStatus,
      legalStatusError,
      profile,
      productPresentationProfile,
      profileSummary,
      recordingModeConfig,
      recordingModeConfigError,
      refresh,
    ],
  );

  return (
    <RegionalBackendContext.Provider value={value}>
      {children}
    </RegionalBackendContext.Provider>
  );
}

/**
 * Consumes the regional backend bootstrap context.
 *
 * @returns {{
 *   backendUser: { uid?: string, email?: string | null, roles?: string[] } | null,
 *   accountContext: { region?: string, appBasePath?: string, dashboardBasePath?: string } | null,
 *   profile: { id?: string | null, clinicStudyId?: string | null } | null,
 *   productPresentationProfile: { id?: string | null, version?: string | null, status?: string } | null,
 *   contextMetadata: {
 *     createdAt?: string | null,
 *     updatedAt?: string | null,
 *     lastLoginAt?: string | null
 *   } | null,
 *   contextVersion: number,
 *   profileSummary: { returning: boolean, latestMs: number | null, newestName: string | null } | null,
 *   recordingModeConfig: {
 *     permittedModes?: string[],
 *     permittedQuestionnaires?: string[],
 *     recordingModeAssignments?: Array<{ mode?: string, required?: boolean, frequency?: string }>,
 *     questionnaireAssignments?: Array<{ questionnaireId?: string, required?: boolean, frequency?: string, showScoreToPatient?: boolean }>,
 *     defaultMode?: string | null,
 *     spirometryVideoRetentionMode?: string | null
 *   } | null,
 *   recordingModeConfigError: string | null,
 *   legalStatus: {
 *     status?: string,
 *     documents?: Record<string, unknown>,
 *     processingRestrictions?: unknown[],
 *     appUseBlocked?: boolean
 *   } | null,
 *   legalStatusError: string | null,
   *   loading: boolean,
   *   error: Error | null,
   *   profileSummaryLoading: boolean,
   *   profileSummaryError: Error | null,
   *   refresh: () => Promise<unknown | null>,
   *   applyProfile: (profilePatch: Record<string, unknown>) => void,
   *   applyLegalStatus: (legalStatus: {
   *     status?: string,
   *     documents?: Record<string, unknown>,
   *     processingRestrictions?: unknown[],
 *     appUseBlocked?: boolean
 *   }) => void
 * }} Shared backend bootstrap state.
 */
export function useRegionalBackend() {
  return useContext(RegionalBackendContext);
}
